import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  normalizeSlotLabel,
  normalizeSlotLabelsCsv,
  sanitizeSlotLabelInput,
} from "@/lib/manufacturing/slotLabel";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

/**
 * Free-text slot label with a touch-friendly suggestions dropdown.
 * Native `<datalist>` is unreliable on mobile browsers, so we always
 * offer a button-triggered list (and optional chips).
 */
export function SlotLabelCombobox({
  value,
  onChange,
  suggestions,
  placeholder = "Slot label",
  disabled,
  className,
  /** When true, show clickable chips that replace the current value. */
  suggestionChips = false,
  chipLimit = 16,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  suggestionChips?: boolean;
  chipLimit?: number;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const inputClass = className ?? `${mfgInlineInputClass} max-w-[140px]`;
  const current = normalizeSlotLabel(value);

  const normalizedSuggestions = useMemo(
    () => [...new Set(suggestions.map((s) => normalizeSlotLabel(s)).filter(Boolean))],
    [suggestions],
  );

  const chips = suggestionChips ? normalizedSuggestions.slice(0, chipLimit) : [];

  const filtered = useMemo(() => {
    const q = current;
    if (!q) return normalizedSuggestions.slice(0, 40);
    return normalizedSuggestions.filter((s) => s.includes(q)).slice(0, 40);
  }, [normalizedSuggestions, current]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  function pick(label: string) {
    onChange(label);
    setOpen(false);
  }

  const input = (
    <div ref={rootRef} className="relative inline-flex max-w-full items-stretch">
      <input
        className={`${inputClass} rounded-r-none`}
        list={listId}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(sanitizeSlotLabelInput(e.target.value))}
        onFocus={() => {
          if (!disabled && normalizedSuggestions.length > 0) setOpen(true);
        }}
        onBlur={() => {
          const n = normalizeSlotLabel(value);
          if (n !== value) onChange(n);
        }}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        disabled={disabled || normalizedSuggestions.length === 0}
        title="Previous slot labels"
        aria-label="Previous slot labels"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          "shrink-0 rounded-r-md border border-l-0 border-gray-200 bg-gray-50 px-1.5 text-xs text-gray-600 " +
          "hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 " +
          "dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
        }
      >
        ▾
      </button>
      <datalist id={listId}>
        {normalizedSuggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {open && filtered.length > 0 ? (
        <ul
          role="listbox"
          className={
            "absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-md border " +
            "border-gray-200 bg-white py-1 shadow-md dark:border-zinc-700 dark:bg-zinc-900"
          }
        >
          {filtered.map((label) => {
            const active = current === label;
            return (
              <li key={label}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={
                    "flex w-full px-2 py-1.5 text-left text-xs " +
                    (active
                      ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                      : "text-gray-800 hover:bg-indigo-50 dark:text-zinc-100 dark:hover:bg-indigo-950/40")
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(label)}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  if (chips.length === 0) return input;

  return (
    <div className="space-y-1">
      {input}
      <div className="flex flex-wrap gap-1">
        {chips.map((label) => {
          const active = current === label;
          return (
            <button
              key={label}
              type="button"
              disabled={disabled}
              title={`Use ${label}`}
              onClick={() => onChange(label)}
              className={
                "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors " +
                (active
                  ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700")
              }
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SlotLabelsMultiCombobox({
  value,
  onChange,
  suggestions,
  placeholder = "Replaces slot labels (comma-sep)",
  disabled,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const listId = useId();
  const inputClass = className ?? `${mfgInlineInputClass} min-w-[180px]`;

  return (
    <>
      <input
        className={inputClass}
        list={listId}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
            .split(",")
            .map((part) => sanitizeSlotLabelInput(part))
            .join(", ");
          onChange(next);
        }}
        onBlur={() => {
          const n = normalizeSlotLabelsCsv(value);
          if (n !== value) onChange(n);
        }}
        autoComplete="off"
        spellCheck={false}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  );
}
